/**
 * "Merlin found answers" (kanban 3eed0be5): the words for a found-answer
 * row and the split between what waits on the reader and what already
 * closed. Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { FoundAnswerEvidence, FoundAnswerRead } from '../../services/qcpFoundAnswers';

/** What kind of item it is, in Jess's words. */
export function kindWord(row: Pick<FoundAnswerRead, 'item_kind'>): string {
  switch (row.item_kind) {
    case 'ask':
      return 'ask';
    case 'case_task':
      return 'checklist step';
    case 'thread_wait':
      return 'wait';
    default:
      return 'item';
  }
}

/** The question's first line (an ask's title; a step's label). */
export function questionTitle(row: Pick<FoundAnswerRead, 'question'>): string {
  return (row.question ?? '').split('\n')[0].trim();
}

/** "QT-10070" / "INV-00003" / "" — where the item lives. */
export function placeWord(row: Pick<FoundAnswerRead, 'thread_no' | 'investigation_no'>): string {
  return row.investigation_no || row.thread_no || '';
}

/** A source id as a word: "meeting", "mail", "order AU02400", "chat"… */
export function sourceWord(evidenceId: string): string {
  const [source, ...rest] = evidenceId.split(':');
  const ref = rest.join(':');
  switch (source) {
    case 'meeting':
      return 'a meeting';
    case 'notes':
      return "Merlin's meeting notes";
    case 'synced_mail':
    case 'sent_mail':
      return 'an email';
    case 'order':
      return `order ${ref}`;
    case 'invoice':
      return 'an invoice';
    case 'complaint':
      return 'the complaint record';
    case 'thread_event':
    case 'case_event':
      return 'the item itself';
    case 'chat':
      return 'your chat with Merlin';
    case 'ask_answer':
      return "someone else's answer";
    default:
      return source || 'a source';
  }
}

/** One line per evidence row: source, date, the quote when there is one. */
export function evidenceLine(e: FoundAnswerEvidence): string {
  const when = e.when ? ` (${e.when})` : '';
  const quote = e.quote ? ` — “${e.quote}”` : '';
  return `${sourceWord(e.id)}${when}${quote}`;
}

/** "9 in 10" from a 0–1 confidence; "" when unknown. */
export function confidenceWord(confidence: number | null): string {
  if (confidence == null || Number.isNaN(confidence)) return '';
  const tenths = Math.round(Math.max(0, Math.min(1, confidence)) * 10);
  return `${tenths} in 10`;
}

/** Who closed it: "Merlin" or "you". */
export function retiredByWord(row: Pick<FoundAnswerRead, 'retired_by'>): string {
  return (row.retired_by ?? '').startsWith('merlin:') ? 'Merlin' : 'you';
}

export interface FoundAnswersSplit {
  /** Waiting on the reader: Confirm or Still open. Oldest first as served. */
  candidates: FoundAnswerRead[];
  /** Already closed with the answer (by Merlin or by the reader), newest first. */
  closed: FoundAnswerRead[];
}

export function splitFoundAnswers(rows: FoundAnswerRead[]): FoundAnswersSplit {
  const candidates = rows.filter((r) => r.status === 'candidate');
  const closed = rows
    .filter((r) => r.status === 'retired')
    .sort((a, b) => (b.retired_at ?? '').localeCompare(a.retired_at ?? ''));
  return { candidates, closed };
}
